import { client } from '../../hooks/Client';
import { useEffect, useState, useMemo } from 'react';
import { db } from '../../firebase/config';
import firebase from '../../firebase/compat';
import ParagraphDevelopmentBarChart from '../Visualisations/development/ParagraphDevelopmentBarChart';
import ParagraphDevelopmentLineChart from '../Visualisations/development/ParagraphDevelopmentLineChart';
import ParagraphDevelopmentPieChart from '../Visualisations/development/ParagraphDevelopmentPieChart';
import ParagraphMomentBarChart from '../Visualisations/moment/ParagraphMomentBarChart';
import ParagraphMomentLineChart from '../Visualisations/moment/ParagraphMomentLineChart';
import ParagraphMomentPieChart from '../Visualisations/moment/ParagraphMomentPieChart';
import defaultDates from '../../utils/defaultDates';
import { resolveColor } from '../../helpers/visualisationDefaults';
import useChartColors from '../../hooks/useChartColors';
import formatMomentLabel from '../../helpers/formatMomentLabel';
import useHierarchyData from '../../hooks/useHierarchyData';
import toMillis from '../../helpers/dataHelpers/matrixDataHelpers/toMillis';

import ChartDataTable from './ChartDataTable';
import { paragraphTable } from '../../helpers/dataHelpers/chartDataTable';
import { definitionCollection } from '../../firebase/publicDefinitions';
const ParagraphData = ({ field, researchId, startDate, endDate, graphType, graphColor, colorPalette, colorOverrides, onTotalResponsesChange, personaId, filterByResearchId }) => {

    // State
    const [data, setData] = useState([]);

    // Palette + overrides (palette defaults to house-style if no override provided)
    const { palette, overrides } = useChartColors({ colorPalette, colorOverrides });

     // Default dates
      const { defaultStartDate, defaultEndDate } = defaultDates();

    // Memoize the research‑IDs array
    const researchIds = useMemo(
        () => (Array.isArray(researchId) ? researchId : [researchId]),
        [researchId]
    );

     // Query responses
     const queryResponses = async () => {
        // Fetch categories
        const categoriesSnapshot = await db.collection('AnalysisCategories')
            .where('CompagnyID', '==', client ? client : "")
            .where('FieldID', '==', field ? field : "")
            .get();
    
        const categories = categoriesSnapshot.docs.map(doc => {
            const data = doc.data();
            return {
                ID: data.ID,
                Categorie: data.Categorie,
            };
        });

    
        // Fetch words
        const wordsSnapshot = await db.collection('AnalysisWords')
            .where('CompagnyID', '==', client ? client : "")
            .where('FieldID', '==', field ? field : "")
            .where('Timestamp', '>=', startDate ? startDate : defaultStartDate)
            .where('Timestamp', '<=', endDate ? endDate : defaultEndDate)
            .get();
    
        const words = wordsSnapshot.docs.map(doc => doc.data());
    
        // Fetch measure moments
        const momentsSnapshot = await definitionCollection('MeasureMoments')
            .where('CompagnyID', '==', client)
             .where('ResearchID', 'in', researchIds)
            .orderBy('Position', 'asc')
            .get();
    
        const moments = momentsSnapshot.docs
            .filter((doc) => !doc.data().Deleted)
            .map((doc) => ({
                id: doc.data().ID,
                name: formatMomentLabel(doc.data().Title, doc.data().Position),
            }));
    
        // Group words by MomentID
        const momentsMap = words.reduce((acc, word) => {
            if (!acc[word.MomentID]) {
                acc[word.MomentID] = [];
            }
            acc[word.MomentID].push(word);
            return acc;
        }, {});
    
        // Transform grouped data
        const groupedData = moments
            .map(moment => {
                const wordsForMoment = momentsMap[moment.id] || [];

                const categoriesForMoment = categories.map(category => {
                    const wordCount = wordsForMoment.filter(word => word.CategoryID === category.ID).length;
                    // Color injected at render time via useMemo — carry the ID here
                    return {
                        Id: category.ID,
                        Title: category.Categorie,
                        Words: wordCount,
                    };
                });

                // Calculate total words for the moment
                const totalWordsForMoment = categoriesForMoment.reduce((sum, category) => sum + category.Words, 0);

                return {
                    MomentName: moment.name, // Use the title of the measure moment
                    Categories: categoriesForMoment,
                    TotalWords: totalWordsForMoment // Add total words for filtering
                };
            })
            .filter(moment => moment.TotalWords > 0); // Exclude moments with no results

        setData(groupedData);
    
    };
    
    
    useEffect(() => {
        queryResponses();
    }, [field, researchId, startDate, endDate]);

    // Het aantal antwoorden komt uit de Cloud Function, niet uit een eigen query:
    // in een gedeelde rapportage leest een bezoeker zonder account, en die mag
    // QuestionnairesResponses niet rechtstreeks lezen. Zelfde parameters als
    // OpenQuestionSlider, zodat ze in de stand 'both' één opgehaalde set delen.
    // AI-CONTEXT: See docs/security-kaart-2026-10-22.md
    const dsMs = useMemo(() => toMillis(defaultStartDate) ?? 0, [defaultStartDate]);
    const deMs = useMemo(() => toMillis(defaultEndDate) ?? 8640000000000000, [defaultEndDate]);
    const { totalRespondents: totalResponses } = useHierarchyData({
        syncType: 'Question',
        id: field,
        startDate: toMillis(startDate) ?? dsMs,
        endDate: toMillis(endDate) ?? deMs,
        researchIds,
        personaId,
        filterByResearchId,
    });

    useEffect(() => {
        onTotalResponsesChange?.(totalResponses);
    }, [totalResponses, onTotalResponsesChange]);

  // Inject colors at render time so palette/override changes don't require refetching
  const coloredData = useMemo(() => {
    return data.map(moment => ({
      ...moment,
      Categories: moment.Categories.map((category, index) => ({
        ...category,
        Color: resolveColor(category.Id, index, palette, overrides),
      })),
    }));
  }, [data, palette, overrides]);

  // Render the correct graph based on the data
  const graphComponents = {
    single: {
      line: <ParagraphMomentLineChart data={coloredData} totalResponses={totalResponses} graphColor={graphColor}/>,
      bar: <ParagraphMomentBarChart data={coloredData} totalResponses={totalResponses} graphColor={graphColor}/>,
      pie: <ParagraphMomentPieChart data={coloredData} totalResponses={totalResponses} graphColor={graphColor}/>
    },
    multiple: {
      line: <ParagraphDevelopmentLineChart rawData={coloredData} totalResponses={totalResponses} graphColor={graphColor}/>,
      bar: <ParagraphDevelopmentBarChart rawData={coloredData} totalResponses={totalResponses} graphColor={graphColor}/>,
      pie: <ParagraphDevelopmentPieChart rawData={coloredData} totalResponses={totalResponses} graphColor={graphColor}/>
    }
  };

  const isSingleData = coloredData.length === 1;
  const typeOfGraph = isSingleData ? 'single' : 'multiple';

return (
    <>
         {graphComponents[typeOfGraph][graphType ? graphType : 'bar'] || null}
         <ChartDataTable table={paragraphTable(data)} />
    </>
);
}

export default ParagraphData